import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FlatList, Pressable, Text, View, RefreshControl } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useFocusEffect } from '@react-navigation/core';
import { useColorScheme } from 'nativewind';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { EventCard, Layout, PostFilter, Pagination, Loader } from '../../components';
import {
  adaptiveContainerPaddingTop,
  isMobile,
  isMobileWeb,
  isNative,
  isTablet,
  isWeb,
  navHeight,
} from '../../utils/responsive';
import { Event, PaginatedResponse } from '../../client/interfaces';
import client from '../../client/client';
import { useMessage } from '../../contexts/Messages/MessageContext';
import {
  NavigationParamList,
  ScreenName,
  StackName,
  StackNavigationScreenProps,
} from '../../navigation/types';
import { distributeElements } from '../../utils';
import { cln } from '../../utils/classnames';
import { useResize } from '../../utils/resize';

import { EventsParamsContext } from '../../contexts/EventsParams/EventsParamsContext';
import { NavigationContextStack } from '../../contexts/NavigationContext/NavigationContextStack';
import useDebounce from '../../hooks/useDebounce';
import analytics from '../../utils/analytics';

const EventsScreen: React.FC<
  StackNavigationScreenProps<NavigationParamList, ScreenName.EventsScreen>
> = ({ navigation, route }) => {
  const { t } = useTranslation();
  const { setMessage } = useMessage();
  const { isWidthBelowSmall, isWidthBelowLarge } = useResize();

  const { setStackNavigation, setStackRoute, stackRoute, changedStack, setChangedStack } =
    useContext(NavigationContextStack);

  const [firstPageLoaded, setFirstPageLoaded] = useState<boolean>(false);
  const [needsRefresh, setNeedsRefresh] = useState(true);
  const initialMountRef = useRef(true);

  useEffect(() => {
    //We only clear the events state when the current stack is the one we're on
    //and if we have come from eventsScreen (list) screen (not the event screen).
    if (stackRoute && changedStack === StackName.EventsStack) {
      if (!isMobile || (isMobile && stackRoute?.name === 'events')) {
        emptyEventsState();
        setFirstPageLoaded(false);
        isMobile && fetchEvents(afterMobileFetch);
      }
      setChangedStack(null);
      setNeedsRefresh(false);
    }
  }, [changedStack, stackRoute]);

  useFocusEffect(
    useCallback(() => {
      setStackRoute(route);
      setStackNavigation(navigation);
    }, [changedStack]),
  );

  // Reset the filter when leaving to another stack not when coming back
  useEffect(() => {
    const unsubscribe = navigation.getParent()?.addListener('blur', () => {
      emptyEventsFilter();
      if (isMobile) setNeedsRefresh(true);
    });
    return unsubscribe;
  }, [navigation]);

  const {
    getEventsParams,
    setGetEventsParams,
    nextPageIdentifier,
    setNextPageIdentifier,
    allEvents,
    setAllEvents,
    emptyGetEventsParams,
    emptyEventsFilter,
    resetInfiniteScroll,
    eventPagination,
    setEventPagination,
    resetEventPagination,
  } = useContext(EventsParamsContext);

  const [refreshing, setRefreshing] = useState(false);
  const { colorScheme } = useColorScheme();

  const insets = useSafeAreaInsets();

  const [isLoading, setIsLoading] = useState(true);
  const [distributedEvents, setDistributedEvents] = useState<Event[][]>([]);
  const [paginatedEvents, setPaginatedEvents] = useState<Event[]>([]);

  const isFilterActive =
    getEventsParams?.tags?.length >= 1 ||
    getEventsParams?.isBookmarked ||
    getEventsParams?.search ||
    getEventsParams?.isPast;

  const isResetPending = isMobile && needsRefresh;

  const flatListRef = useRef<FlatList>(null);
  const debouncedSearch = useDebounce(getEventsParams.search);

  const lastLoggedSearch = useRef<string | null>(null);

  const fetchEvents = async (
    afterFetchCallback: (paginatedEvents: PaginatedResponse<Event>) => void,
  ) => {
    try {
      setIsLoading(true);
      const paginatedEventsResponse = await client.getEvents(getEventsParams);
      afterFetchCallback(paginatedEventsResponse);
      setEventPagination(paginatedEventsResponse.pagination);
      setDistributedEvents(
        distributeElements(
          paginatedEventsResponse.items,
          isWidthBelowSmall ? 1 : isWidthBelowLarge ? 2 : 3,
        ),
      );

      if (getEventsParams.search) {
        if (lastLoggedSearch.current !== getEventsParams.search) {
          lastLoggedSearch.current = getEventsParams.search;
          await analytics().logEvent('search', {
            search_term: getEventsParams.search,
            content_type: 'event',
            results_count: paginatedEventsResponse.pagination.total,
            has_results: paginatedEventsResponse.pagination.total > 0 ? 1 : 0,
          });
        }
      } else {
        lastLoggedSearch.current = null;
      }
    } catch (error) {
      setMessage({ message: error.message, type: 'error' });
    }
    setTimeout(() => {
      setIsLoading(false);
      !firstPageLoaded && setFirstPageLoaded(true);
    }, 200);
  };

  useEffect(() => {
    setDistributedEvents(
      distributeElements(paginatedEvents, isWidthBelowSmall ? 1 : isWidthBelowLarge ? 2 : 3),
    );
  }, [isWidthBelowLarge, isWidthBelowSmall]);

  const afterMobileFetch = async (paginatedEvents: PaginatedResponse<Event>) => {
    const shouldAppend = getEventsParams.page === nextPageIdentifier;

    setAllEvents((prev) => {
      const existingIds = new Set(prev.map((a) => a.id));
      const incomingIds = new Set(paginatedEvents.items.map((a) => a.id));

      const newItems = paginatedEvents.items.filter((a) => !existingIds.has(a.id));

      // Ha append (scroll), csak fűzzük az újakat
      if (shouldAppend) {
        return [...prev, ...newItems];
      }

      // Ha page === 1 és nem appendelünk, akkor új keresés történt
      // Megtartjuk azokat a régi elemeket, amik benne vannak az új találatokban
      const preservedItems = prev.filter((a) => incomingIds.has(a.id));

      // Új lista: megtartott + újonnan érkezett, de nem duplikált
      return [...preservedItems, ...newItems];
    });
  };

  const afterDesktopFetch = async (paginatedEvents: PaginatedResponse<Event>) => {
    setPaginatedEvents(paginatedEvents.items);
  };

  const toggleBookmark = (event: Event) => {
    const setter = isMobile ? setAllEvents : setPaginatedEvents;

    setter((prev) =>
      prev.map((a) => (a.id === event.id ? { ...a, isBookmarked: !a?.isBookmarked } : a)),
    );

    if (!isMobile) {
      setDistributedEvents((prev) =>
        prev.map((row) =>
          row.map((a) => (a.id === event.id ? { ...a, isBookmarked: !a?.isBookmarked } : a)),
        ),
      );
    }
  };

  const onScrollEndReached = () => {
    const nextPage = eventPagination.currentPage + 1;

    if (nextPage <= eventPagination.pageCount) {
      setNextPageIdentifier(nextPage);
    }
  };

  useEffect(() => {
    if (isMobile && initialMountRef.current) {
      initialMountRef.current = false;
      return;
    }
    isMobile ? fetchEvents(afterMobileFetch) : fetchEvents(afterDesktopFetch);
  }, [JSON.stringify(getEventsParams)]);

  // Keep a live ref to fetchEvents so the focus-effect refetch below uses the
  // current params instead of the empty ones captured on first render (which
  // caused an unfiltered list after returning from an event on web).
  const fetchEventsRef = useRef(fetchEvents);
  fetchEventsRef.current = fetchEvents;

  useFocusEffect(
    useCallback(() => {
      if (!isMobile) {
        setPaginatedEvents([]);
        fetchEventsRef.current(afterDesktopFetch);
      }
    }, []),
  );

  useEffect(() => {
    if (isMobile) {
      flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
    }
  }, [
    getEventsParams.search,
    getEventsParams.tags,
    getEventsParams.isBookmarked,
    getEventsParams.isPast,
  ]);

  useEffect(() => {
    if (nextPageIdentifier > eventPagination.currentPage) {
      setGetEventsParams((prev) => ({ ...prev, page: nextPageIdentifier }));
    }
  }, [nextPageIdentifier]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchEvents(afterMobileFetch);
    setTimeout(() => {
      setRefreshing(false);
    }, 800);
  }, [debouncedSearch]);

  const emptyEventsState = (hard = true) => {
    setIsLoading(true);
    hard && setGetEventsParams({ ...emptyGetEventsParams });
    setAllEvents([]);
    setPaginatedEvents([]);
    resetInfiniteScroll();
    resetEventPagination();
  };

  const renderEventItem = useCallback(
    ({ item }) => (
      <EventCard
        item={item}
        toggleCardMark={toggleBookmark}
        styleProp={{
          flex: isTablet ? 0.5 : 1,
          marginBottom: 40,
        }}
      />
    ),
    [toggleBookmark, isTablet],
  );

  const mobileFeed =
    (isLoading && !firstPageLoaded) || isResetPending ? (
      <View className={cln('w-full h-full items-center justify-center', isTablet && 'pb-[320px]')}>
        <Loader />
      </View>
    ) : (
      <View
        style={{
          flex: 1,
          overflow: 'hidden',
        }}
        testID="events-screen-mobile"
      >
        <FlatList
          data={allEvents}
          keyExtractor={(item) => item.id}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              progressViewOffset={100}
              tintColor={colorScheme === 'dark' ? '#b0b0b0' : '#d1d1d1'}
              colors={colorScheme === 'dark' ? ['#b0b0b0'] : ['#f6f6f6']}
              progressBackgroundColor={
                colorScheme === 'dark' ? 'rgba(79,79,79,0.9)' : 'rgba(136, 136, 136,0.9)'
              }
            />
          }
          renderItem={renderEventItem}
          onEndReached={onScrollEndReached}
          onEndReachedThreshold={0.8}
          initialNumToRender={emptyGetEventsParams.perPage}
          ListFooterComponent={isLoading ? Loader : null}
          ref={flatListRef}
          numColumns={isTablet ? 2 : 1}
          columnWrapperStyle={
            isTablet ? { flex: 1, justifyContent: 'space-between', gap: 24 } : null
          }
          contentContainerStyle={{
            paddingHorizontal: 30,
            paddingTop:
              !isTablet && !isFilterActive
                ? navHeight + insets.top + adaptiveContainerPaddingTop
                : 8,
          }}
        />
      </View>
    );

  const desktopFeed = isLoading ? (
    <Loader />
  ) : (
    <View testID="events-screen-web-desktop">
      <View
        className="flex flex-row flex-wrap md:mx-[24px]"
        style={{
          rowGap: 64,
          columnGap: 24,
          justifyContent: 'flex-start',
          marginTop: isMobileWeb ? 24 : 0,
        }}
      >
        {distributedEvents.map((column, index) => (
          <View className={cln('flex-1')} key={index}>
            {column.map((item) => (
              <EventCard
                key={item.id}
                item={item}
                toggleCardMark={toggleBookmark}
                styleProp={{ marginBottom: isMobile ? 40 : 64 }}
              />
            ))}
          </View>
        ))}
      </View>
      <Pagination
        data={eventPagination}
        paramSetter={setGetEventsParams}
        params={getEventsParams}
      />
    </View>
  );

  const feed = isMobile ? mobileFeed : desktopFeed;

  const emptySearch = (
    <>
      {!isLoading && (
        <View
          className={cln('flex-column px-[32px]', isMobile && !isFilterActive && 'pt-[120px]')}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          style={{ display: isWeb ? 'inline' : 'flex' }}
        >
          <Text className="text-h3 md:text-h2 text-primaryLight-500 dark:text-primary-500 font-[sans-600]">
            {t('articles:no_results')}{' '}
          </Text>
          <Text className="text-h3 md:text-h2 text-neutral-950 dark:text-neutral-50 font-[sans-600]">
            {t('articles:based_on_your_search')}.
          </Text>
        </View>
      )}
    </>
  );

  return (
    <Layout
      title={!isMobile && t('events:title')}
      isScrollView={!isMobile}
      onPressSearch={() =>
        navigation.navigate(ScreenName.PostFilterScreen, { fromScreen: ScreenName.EventsScreen })
      }
      isListScreen={true}
    >
      {(getEventsParams?.tags?.length >= 1 ||
        getEventsParams.isBookmarked ||
        getEventsParams.search ||
        getEventsParams.isPast) &&
      !isTablet &&
      isMobile ? (
        <View
          className="flex-row self-end px-[30px]"
          style={{ paddingTop: isNative ? navHeight + insets.top : isMobile ? 56 : 0 }}
        >
          <Text className="text-neutral-950 dark:text-neutral-50 text-body font-[sans-700] mr-1.5">
            {eventPagination.total}
          </Text>
          <Text className="text-neutral-950 dark:text-neutral-50 text-body font-[sans-400]">
            {t('articles:results')}
          </Text>
        </View>
      ) : (
        <></>
      )}
      {/* REMOVE ALL BUTTON */}
      {(getEventsParams?.tags?.length >= 1 ||
        getEventsParams.isBookmarked ||
        getEventsParams.search ||
        getEventsParams.isPast) &&
      !isTablet &&
      isMobile ? (
        <View className="px-[30px]">
          <Pressable
            className="justify-center items-center"
            onPress={emptyEventsFilter}
            style={{
              alignSelf: 'flex-end',
            }}
          >
            <Text className="text-primary-600 dark:text-primary-500 text-body font-[sans-400] py-2">
              {t('articles:remove_search')}
            </Text>
          </Pressable>
        </View>
      ) : (
        <></>
      )}

      {!isMobile || isTablet ? (
        <PostFilter
          getPostsParams={getEventsParams}
          setGetPostsParams={setGetEventsParams}
          resetInfiniteScroll={resetInfiniteScroll}
          pagination={eventPagination}
          screen={ScreenName.EventsScreen}
        />
      ) : (
        <></>
      )}
      {firstPageLoaded && eventPagination.total === 0 ? emptySearch : feed}
    </Layout>
  );
};

export default EventsScreen;
